import { FC, useState } from 'react';
import { Input } from 'reactstrap';
import ConsoleFitBadge from './ConsoleFitBadge';
import ConsoleInterviewBadge from './ConsoleInterviewBadge';
import CandidateQuickEditModal from './CandidateQuickEditModal';
import { VIEW_CANDIDATE_ROUTE } from '../../utils/constant';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApplicantRowData {
  pipelineEntryId: string;
  jobApplicantId: string;
  candidateId: string;
  fullName: string | null | undefined;
  email: string | null | undefined;
  phone: string | null | undefined;
  callPhone: string | null | undefined;
  currentCompany: string | null | undefined;
  currentTitle: string | null | undefined;
  aiScore: number | null | undefined;
  aiScoreReason: string | null | undefined;
  aiScoredResumeId: string | null | undefined;
  aiScoredAt: string | null | undefined;
  interviewRecommendation: string | null | undefined;
  interviewScore: number | null | undefined;
  status: string;
  needsHumanReason: string | null | undefined;
  assignedRecruiterId: string | null | undefined;
  assignedRecruiterName: string | null | undefined;
  justAdded: boolean | null | undefined;
  createdAt: string;
}

interface Props {
  row: ApplicantRowData;
  selected: boolean;
  onToggleSelect: (pipelineEntryId: string) => void;
  onOpenTrail: (pipelineEntryId: string, candidateName: string | null | undefined, candidateTitle: string | null | undefined) => void;
  onRescored: () => void;
}

// ---------------------------------------------------------------------------
// Status → reactstrap Badge color + label map
// ---------------------------------------------------------------------------

type StatusTone = 'neutral' | 'warn' | 'danger' | 'info' | 'active';

interface StatusDisplay {
  tone: StatusTone;
  label: string;
}

const STATUS_DISPLAY: Record<string, StatusDisplay> = {
  NEW: { tone: 'neutral', label: 'New' },
  SEND_OUTREACH: { tone: 'warn', label: 'Send outreach' },
  DECLINED_BY_ADMIN: { tone: 'danger', label: 'Declined by admin' },
  NEEDS_HUMAN_OUTREACH: { tone: 'info', label: 'Needs human outreach' },
  ASSIGNED_TO_RECRUITER: { tone: 'active', label: 'Assigned to recruiter' },
  // Reserved statuses — render neutrally if encountered
  IN_QUEUE: { tone: 'neutral', label: 'In queue' },
  OUTREACH_SENT: { tone: 'neutral', label: 'Outreach sent' },
  AWAITING_RESPONSE: { tone: 'neutral', label: 'Awaiting response' },
  SCHEDULED: { tone: 'neutral', label: 'Scheduled' },
  INTERVIEWED: { tone: 'neutral', label: 'Interviewed' },
  COMPLETED: { tone: 'neutral', label: 'Completed' },
};

const NEEDS_HUMAN_REASON_LABEL: Record<string, string> = {
  NO_EMAIL: 'No email on file',
  SEND_FAILED: 'Send failed',
  ACTIVE_DECLINE: 'Active decline',
  NO_RESPONSE: 'No response',
  UNREACHABLE_NO_REBOOK: 'Unreachable — no rebook',
  UNREACHABLE_MISSED_AGAIN: 'Unreachable — missed again',
  SILENT_CALL: 'Silent call — never spoke',
};

const getStatusDisplay = (status: string): StatusDisplay =>
  STATUS_DISPLAY[status] ?? { tone: 'neutral', label: status };

// ---------------------------------------------------------------------------
// Avatar initials helper
// ---------------------------------------------------------------------------

const initials = (fullName: string | null | undefined): string => {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
};

// Format a stored phone (digits) for display: (XXX) XXX-XXXX for US numbers.
const formatPhone = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1')
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApplicantRow: FC<Props> = ({
  row,
  selected,
  onToggleSelect,
  onOpenTrail,
  onRescored,
}) => {
  const statusDisplay = getStatusDisplay(row.status);
  const [editingContact, setEditingContact] = useState(false);

  const rowClass = [
    'ai-console__row',
    'ai-console__row-grid',
    selected ? 'ai-console__row--selected' : '',
    row.justAdded ? 'ai-console__row--just-added' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={rowClass}>
      {/* Checkbox */}
      <div className="ai-console__col-check">
        <Input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(row.pipelineEntryId)}
        />
      </div>

      {/* Avatar */}
      <div className="ai-console__col-avatar">
        <span className="ai-console__avatar">{initials(row.fullName)}</span>
      </div>

      {/* Candidate cell — name only */}
      <div className="ai-console__col-candidate">
        <div className="ai-console__candidate-name">
          <a
            href={`${VIEW_CANDIDATE_ROUTE}/${row.candidateId}`}
            className="ai-console__name-link"
            target="_blank"
            rel="noreferrer"
          >
            {row.fullName ?? '—'}
          </a>
          {row.justAdded && (
            <span className="ai-console__just-added-chip">JUST ADDED</span>
          )}
          <button
            type="button"
            className="ai-console__contact-edit-btn"
            title="Edit email / phone"
            onClick={() => setEditingContact(true)}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M11.5 2.5l2 2L6 12l-2.7.7.7-2.7 7.5-7.5z"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
            <span className="sr-only">Edit contact</span>
          </button>
        </div>
      </div>

      {/* Contact cell — email over phone */}
      <div className="ai-console__col-contact">
        <div className="ai-console__candidate-email">
          {row.email ? (
            <a href={`mailto:${row.email}`} className="ai-console__email-link">
              {row.email}
            </a>
          ) : (
            <span className="ai-console__no-email">— no email on file —</span>
          )}
        </div>
        {row.phone && (
          <div className="ai-console__candidate-phone">
            {formatPhone(row.phone)}
          </div>
        )}
        {/* Call-specific number the candidate confirmed on the scheduling page —
            shown only when it differs from the on-file number (S2-SP-11). This is
            the number the AI recruiter will actually dial. */}
        {row.callPhone &&
          row.callPhone.replace(/\D/g, '') !==
            (row.phone ?? '').replace(/\D/g, '') && (
            <div
              className="ai-console__candidate-phone ai-console__candidate-callphone"
              title="Number the candidate confirmed on the scheduling page — this is what the AI recruiter will call"
            >
              Call #: {formatPhone(row.callPhone)}
            </div>
          )}
      </div>

      {/* Current company / title */}
      <div className="ai-console__col-company">
        {row.currentCompany && (
          <div className="ai-console__current-company">{row.currentCompany}</div>
        )}
        {row.currentTitle && (
          <div className="ai-console__current-title text-muted">{row.currentTitle}</div>
        )}
        {!row.currentCompany && !row.currentTitle && (
          <span className="text-muted">—</span>
        )}
      </div>

      {/* Fit badge */}
      <div className="ai-console__col-fit">
        <ConsoleFitBadge
          jobApplicantId={row.jobApplicantId}
          score={row.aiScore ?? null}
          reason={row.aiScoreReason}
          scoredResumeId={row.aiScoredResumeId}
          scoredAt={row.aiScoredAt}
          onRescored={onRescored}
        />
      </div>

      {/* Recommendation cell — interview-result badge.
          Returns null unless the row has an interview recommendation, so
          non-completed rows render an empty cell. */}
      <div className="ai-console__col-recommendation">
        <ConsoleInterviewBadge
          recommendation={row.interviewRecommendation}
          score={row.interviewScore}
          pipelineEntryId={row.pipelineEntryId}
          candidateName={row.fullName}
        />
      </div>

      {/* Status cell */}
      <div className="ai-console__col-status">
        <span className={`ai-console__pill ai-console__pill--${statusDisplay.tone}`}>
          {statusDisplay.label}
        </span>
        {row.status === 'NEEDS_HUMAN_OUTREACH' && row.needsHumanReason && (
          <div className="ai-console__status-sub text-muted">
            {NEEDS_HUMAN_REASON_LABEL[row.needsHumanReason] ?? row.needsHumanReason}
          </div>
        )}
        {row.status === 'ASSIGNED_TO_RECRUITER' && row.assignedRecruiterName && (
          <div className="ai-console__status-sub text-muted">
            &rarr; {row.assignedRecruiterName}
          </div>
        )}
      </div>

      {/* Trail icon */}
      <div className="ai-console__col-trail">
        <button
          type="button"
          className="ai-console__trail-btn"
          title="View action trail"
          onClick={() =>
            onOpenTrail(row.pipelineEntryId, row.fullName, row.currentTitle)
          }
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.3" />
            <path
              d="M8 4.75V8l2.25 1.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="sr-only">View action trail</span>
        </button>
      </div>
      </div>

      <CandidateQuickEditModal
        isOpen={editingContact}
        candidateId={row.candidateId}
        fullName={row.fullName}
        email={row.email}
        phone={row.phone}
        currentCompany={row.currentCompany}
        currentTitle={row.currentTitle}
        onClose={() => setEditingContact(false)}
        onSaved={() => {
          setEditingContact(false);
          onRescored();
        }}
      />
    </>
  );
};

export default ApplicantRow;
